import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { PIconModule } from '@panaya/ui/p-icon';
import { CanvasFrameStore } from '@panaya/ai-chat/features';
import { CreateResultStatus } from '@panaya/ai-chat/shared';
import { DraftContentContributor } from '../draft-content.contributor';
import { DraftStore } from '../draft.store';

interface DraftCardView {
    /** The agent's text for the draft, or the library's "Drafts". */
    title: string | null;
    countKey: string;
    count: number;
    version: number;
    stateKey: string;
}

const STATE_KEY_BY_RESULT: Record<CreateResultStatus, string> = {
    created: 'aichat.canvas.draft.card.created',
    partially_failed: 'aichat.canvas.draft.card.partially_failed',
    failed: 'aichat.canvas.draft.card.failed'
};

/**
 * The draft card at the end of the chat log: "Vendor payment process · 7 tests · V1 ·
 * latest", then "Created" once the canvas closed with a result. Hidden until a draft arrives.
 */
@Component({
    standalone: true,
    selector: 'p-ai-chat-draft-card',
    imports: [TranslateModule, PIconModule],
    templateUrl: './draft-card.component.html',
    styleUrl: './draft-card.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DraftCardComponent {
    private readonly draftStore = inject(DraftStore);

    private readonly frameStore = inject(CanvasFrameStore);

    private readonly content = inject(DraftContentContributor);

    protected readonly card = computed((): DraftCardView | null => {
        const draft = this.draftStore.draft();

        if (!draft) {
            return null;
        }

        const result = this.frameStore.closeResult();

        return {
            title: draft.title ?? null,
            countKey: this.content.countLabelKey(),
            count: draft.entities.length,
            version: draft.version,
            stateKey: result ? STATE_KEY_BY_RESULT[result.status] : 'aichat.canvas.draft.card.latest'
        };
    });
}
